import React from 'react';
import shortid from 'shortid';
import { get, cloneDeep, isEmpty, findIndex, assign, map, forEach, isFunction } from 'lodash';

// local dependencies
import { overlay as overlayView } from './overlay.view';
import { messageBus } from 'core/modules/message-bus';
import { animator, AnimationManager } from 'core/modules/animation';
import { isMobile, isApp } from 'core/modules/device-detect';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';
import { STATUS_BAR_THEME } from 'constants/common';
import { ANIMATION_NAMES } from 'constants/animation';
import { addClass, removeClass } from 'utils/dom';
import { overlayViewResolver } from 'resolvers/overlayView';
import { isEscapeKey } from 'utils/keyChecker';
import { OVERLAY_VIEWS } from 'constants/templates';

// overlay card with animation
const OverlayView = animator( overlayView, ANIMATION_NAMES.FADE_IN_SLIDE_Y, { distance: isMobile() ? 10 : 30 } );

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 *
 * All the config related things will go in settings, rest of the data will be set in overlaydata.
 *
 * Sample usage of overlay-container:
 *
 * messageBus.trigger( MESSAGE_BUS_EVENTS.OPEN_OVERLAY, {
 *   settings: {
 *       modalType: OVERLAY_EVENTS.CLAIMS_DETAILS,
 *       showCloseButton: true,
 *    },
 *    data: { ...this.state, ...this.props, ...anyComponentData }
 * 	} );
 *
 * @example:
 * import { OverlayMounter } from 'modules/overlay';
 *
 * // render
 * <OverlayMounter propName='propValue'/>
 */
export class OverlayMounter extends React.Component {

    /**
     * @param {object} props - props
     */
    constructor( props ) {
        log.debug( 'OverlayMounter.constructor()' );

        super( props );

        // bind class methods to instance (this)
		this.__bind();

        // component state
        this.state = {

            // data and settings of each overlay window
            windows: []
        };
	}


    /**
     * @desc bind class methods to instance (this)
     */
    __bind() {
        log.debug( 'OverlayMounter.__bind()' );

        this.handleOpen = this.handleOpen.bind( this );
        this.handleUpdate = this.handleUpdate.bind( this );
		this.handleClose = this.handleClose.bind( this );
		this.getComponent = this.getComponent.bind( this );
		this.handleKeyUp = this.handleKeyUp.bind( this );
    }

    /**
     * @desc Get overlay view based on `modalType`
     */
    getComponent( modalType ) {
        log.debug( 'OverlayMounter.getView()' );

		return overlayViewResolver( modalType );
    }

    /**
     * @desc Open an overlay view.
     */
    handleOpen( { settings, data, config } ) {
        log.debug( `Overlay.handleOpen(${ settings.modalType })` );

        // variables
        let windows = cloneDeep( this.state.windows );
        const { modalType, showCloseButton, stack, enablePadding, hideOverlay, transparentOverlay, transparentBackground, onClose, ignoreOverflow, ignoreOutsideClick } = settings;
        const statusBarTheme = get( settings, 'statusBarTheme', STATUS_BAR_THEME.DARK );

        // overlay window data and config
        const overlayWindowConfig = {
            id: get( settings, 'id', shortid.generate() ),
			showCloseButton,
            stack,
            hideOverlay,
            transparentOverlay,
            transparentBackground,
			statusBarTheme,
            modalType,
            enablePadding,
            onClose,
			data,
            config,
            ignoreOverflow,
            ignoreOutsideClick,
            view: this.getComponent( modalType ),
            fixedToBottom: get( settings, 'fixedToBottom', false ),
            fixedToCenter: get( settings, 'fixedToCenter', false ),
            renderInfoIcon: get( settings, 'renderInfoIcon', false )
        };

        // check overlay window by modalType: 'ERROR_NO_PERMISSIONS' already present
        const noPermissionsWindowIndex = findIndex( windows, { modalType: OVERLAY_VIEWS.ERROR_NO_PERMISSIONS } );

        if( -1 === noPermissionsWindowIndex ) { // if 'ERROR_NO_PERMISSIONS' not already open

            // generate new `windows` state based on whether overlay needs to be stacked or not
            if( true === stack ) {
                windows.push( overlayWindowConfig ); // push new window
            } else {
                windows = [ overlayWindowConfig ]; // replace with new window
            }

        } else {
            return null; // if 'ERROR_NO_PERMISSIONS' already open :- dont open another overlay
        }

        // update `windows` in the state
        this.setState( { windows }, () => {

            // add overlay state class on `body`
			addClass( document.body, 'mode--overlay-open' );

			document.addEventListener( 'keyup', this.handleKeyUp );

            // set status-bar theme based on currently opening overlay config
            if( isApp() ) {

                //statusBar.setTheme( statusBarTheme );
            }
        } );
	}

	handleKeyUp( event ) {
        log.debug( 'OverlayMounter.handleKeyUp()' );

		if( ( 'key' in event ) && ( isEscapeKey( event.key ) ) ) {
			this.handleClose();
		}
	}

    /**
     * @desc Update overlay data
     */
    handleUpdate( { id, data } ) {
		log.debug( 'OverlayMounter.handleUpdate()' );

        // variables
        const windows = cloneDeep( this.state.windows );

        // get overlay window by id
        const windowIndex = findIndex( windows, { id } );

        // if `window` exists in `windows` array
        if( -1 !== windowIndex ) {

            // current window config
            const currentOverlayWindowConfig = windows[ windowIndex ];

            // create new window config
            const newOverlayWindowConfig = assign( currentOverlayWindowConfig, {
                data: {
                    ...currentOverlayWindowConfig.data,
                    ...data,
                }
            } );

            // update window config back in array
            windows[ windowIndex ] = newOverlayWindowConfig;

            // update `windows` in the state
            this.setState( { windows } );
        }
    }

    /**
     * @desc Close an overlay view.
     */
    handleClose( { closeAll = false } = {} ) {
        log.debug( 'OverlayMounter.handleClose()' );

        // clone `windows` from the state
        //const activeWindows = cloneDeep( windows.length );
        let windows = cloneDeep( this.state.windows );
        let onCloseCallbacks = [];  // array of callbacks which are triggered on closing the overlay

        // perform close operation only when least one overlay window is open
        if( ! isEmpty( windows ) ) {

            // based on `closeAll` value, pop last active overlay window or empty the array (to close all the windows)
            if( closeAll ) {
                onCloseCallbacks = map( this.state.windows, [ 'onClose' ] );
				windows = [];
            } else {

                const poppedWindow = windows.pop();
				onCloseCallbacks.push( poppedWindow.onClose );
            }

            // update `windows` in the state
            this.setState( { windows }, () => {

                // run `onClose` callbacks
                forEach( onCloseCallbacks, ( onCloseCallback ) => {
                    if( isFunction( onCloseCallback ) ) {
                        onCloseCallback();
					}

                } );

                // remove overlay state class on `body` (when all windows are closed)
                if( isEmpty( windows ) ) {
					removeClass( document.body, 'mode--overlay-open' );
					document.removeEventListener( 'keyup', this.handleKeyUp );

                }

               // reset `status-bar` theme to previous state
                if( isApp() ) {

                    // if multiple windows were closed, reset statusbar theme that many times
                    // consider `300ms` animation duration of the overlay
                    // setTimeout( () => {
                    //     times( activeWindows - windows.length ).forEach( statusBar.resetTheme );
                    // }, 300 );
                }
            } );
        }
    }

    /************************************************************/

    /**
     * @desc When component mounts, listen to message bus events
     */
    componentDidMount() {
        log.debug( 'OverlayMounter.componentDidMount()' );

        messageBus.on( MESSAGE_BUS_EVENTS.OPEN_OVERLAY, this.handleOpen, this );
        messageBus.on( MESSAGE_BUS_EVENTS.UPDATE_OVERLAY, this.handleUpdate, this );
        messageBus.on( MESSAGE_BUS_EVENTS.CLOSE_OVERLAY, this.handleClose, this );
    }

    /**
     * @desc When component unmounts, unlisten to message bus events
     */
    componentWillUnmount() {
        log.debug( 'OverlayMounter.componentWillUnmount()' );

        messageBus.off( MESSAGE_BUS_EVENTS.OPEN_OVERLAY, this.handleOpen, this );
        messageBus.off( MESSAGE_BUS_EVENTS.UPDATE_OVERLAY, this.handleUpdate, this );
        messageBus.off( MESSAGE_BUS_EVENTS.CLOSE_OVERLAY, this.handleClose, this );
    }

    /**
     * @desc Render overlay windows based on `state.windows` list
     */
    render() {
        log.debug( 'OverlayMounter.render()' );

        return (
            <AnimationManager>
                {
                    this.state.windows.map( ( windowConfig ) => {
                        return (
                            <OverlayView
                                key={ windowConfig.id }
                                data={ windowConfig.data }
                                component={ windowConfig.view }
                                closeOverlay={ this.handleClose }
                                hideOverlay={ windowConfig.hideOverlay }
                                transparentOverlay={ windowConfig.transparentOverlay }
                                transparentBackground={ windowConfig.transparentBackground }
                                showCloseButton={ windowConfig.showCloseButton }
                                enablePadding={ windowConfig.enablePadding }
                                fixedToBottom={ windowConfig.fixedToBottom }
                                fixedToCenter={ windowConfig.fixedToCenter }
                                renderInfoIcon={ windowConfig.renderInfoIcon }
                                ignoreOverflow={ windowConfig.ignoreOverflow }
                                ignoreOutsideClick={ windowConfig.ignoreOutsideClick }
								config = { windowConfig.config }
                            />
                        );
                    } )
                }
            </AnimationManager>
        );
    }
}
